import { useEffect, useState } from "react";
import { portalApi } from "@/lib/api";
import { usePortal } from "@/portal/PortalContext";
import { Card, Loading, Empty } from "@/components/Shared";
import { ProductThumb } from "@/pages/QuoteBuilder";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Minus, Package } from "lucide-react";

export default function PortalCatalog() {
  const { cart, addToCart, setQty, me } = usePortal();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  useEffect(() => { portalApi.get("/portal/catalog/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => portalApi.get(`/portal/catalog?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}`).then((r) => setItems(r.data)), 200);
    return () => clearTimeout(t);
  }, [q, category]);
  const inCart = (id) => cart.find((l) => l.product_id === id)?.quantity || 0;

  return (
    <div data-testid="portal-catalog">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#101D51] tracking-tight">Catalogue</h1>
        <p className="text-sm text-[#6B7189] mt-1">Prices shown are your <b>{me?.price_book}</b> rates (before GST). {me?.open_pos > 0 && <span>· {me.open_pos} PO{me.open_pos > 1 ? "s" : ""} awaiting acceptance</span>}</p>
      </div>
      <div className="relative mb-3"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, code or brand…" className="pl-9 h-11 bg-white" data-testid="portal-search" /></div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-3">
        <Chip active={!category} onClick={() => setCategory("")}>All</Chip>
        {meta?.categories.map((c) => <Chip key={c.name} active={category === c.name} onClick={() => setCategory(c.name)}>{c.name} <span className="opacity-60">{c.count}</span></Chip>)}
      </div>
      {!items ? <Loading /> : items.length === 0 ? <Empty icon={Package} title="No products" hint="Try another search or category." /> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="portal-products">
          {items.map((p) => {
            const n = inCart(p.id);
            return (
              <Card key={p.id} className="p-3 flex flex-col" data-testid={`portal-product-${p.code}`}>
                <div className="aspect-square rounded-lg bg-[#F5F6FA] border border-[#E6E8F0] mb-2 overflow-hidden flex items-center justify-center">
                  <ProductThumb url={p.image_url} size={full} className="!w-full !h-full !border-0 !bg-transparent" />
                </div>
                <div className="text-[11px] text-[#6B7189] font-mono truncate">{p.code}{p.brand ? ` · ${p.brand}` : ""}</div>
                <div className="text-sm font-medium text-[#101D51] leading-snug line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                <div className="flex items-end justify-between mt-2">
                  <div><div className="font-bold text-[#101D51]" data-testid={`portal-price-${p.code}`}>{formatINR(p.book_price)}</div><div className="text-[10px] text-[#6B7189]">per {p.unit} · GST {Math.round(p.gst_rate * 100)}%</div></div>
                  {n === 0
                    ? <Button size="sm" className="h-9 bg-[#0B7C5A] hover:bg-[#09664a]" onClick={() => { addToCart(p); toast.success(`${p.name} added`); }} data-testid={`portal-add-${p.code}`}><Plus className="w-4 h-4" /> Add</Button>
                    : <div className="flex items-center rounded-lg border border-[#0B7C5A] overflow-hidden" data-testid={`portal-qty-${p.code}`}>
                      <button className="w-9 h-9 flex items-center justify-center text-[#0B7C5A]" onClick={() => setQty(p.id, n - 1)} data-testid={`portal-minus-${p.code}`}><Minus className="w-4 h-4" /></button>
                      <span className="w-8 text-center text-sm font-semibold text-[#101D51]">{n}</span>
                      <button className="w-9 h-9 flex items-center justify-center text-[#0B7C5A]" onClick={() => setQty(p.id, n + 1)} data-testid={`portal-plus-${p.code}`}><Plus className="w-4 h-4" /></button>
                    </div>}
                </div>
              </Card>);
          })}
        </div>)}
    </div>
  );
}

const full = "full";

function Chip({ active, children, ...p }) {
  return <button {...p} className={`shrink-0 min-h-[36px] px-3.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#0B7C5A] text-white border-[#0B7C5A]" : "bg-white text-[#6B7189] border-[#E6E8F0]"}`}>{children}</button>;
}
